/**
 * Pagination Component
 *
 * A reusable pagination component with Previous/Next navigation and current page indicator.
 * Provides simple navigation controls for paginated data.
 */

import { Text, IconButton, ButtonType, OverlayMode, IconSize, ChevronLeft, ChevronRight } from '@soteria/design-system-ve';

import * as styles from './Pagination.css.js';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

export function Pagination({ currentPage, totalPages, onPageChange, disabled = false }: PaginationProps) {
  // Don't render pagination if there's only one page or no pages
  if (totalPages <= 1) {
    return null;
  }

  // Ensure currentPage is within valid bounds
  const safePage = Math.max(1, Math.min(currentPage, totalPages));

  const handlePrevious = () => {
    if (safePage > 1 && !disabled) {
      onPageChange(safePage - 1);
    }
  };

  const handleNext = () => {
    if (safePage < totalPages && !disabled) {
      onPageChange(safePage + 1);
    }
  };

  const isPreviousDisabled = disabled || safePage <= 1;
  const isNextDisabled = disabled || safePage >= totalPages;

  return (
    <div className={styles.paginationContainer}>
      <IconButton
        aria-label="Previous page"
        buttonType={ButtonType.Secondary}
        disabled={isPreviousDisabled}
        icon={ChevronLeft}
        iconSize={IconSize.Small}
        overlayMode={OverlayMode.Solid}
        title="Previous page"
        onClick={handlePrevious}
      />

      <Text className={styles.pageIndicator}>
        Page {safePage} of {totalPages}
      </Text>

      <IconButton
        aria-label="Next page"
        buttonType={ButtonType.Secondary}
        disabled={isNextDisabled}
        icon={ChevronRight}
        iconSize={IconSize.Small}
        overlayMode={OverlayMode.Solid}
        title="Next page"
        onClick={handleNext}
      />
    </div>
  );
}
